import React, { forwardRef } from 'react';
import dataQa from '@platform-ui/dataQa';
import { useId } from '@platform-ui/generateId';
import InputBox from '@platform-ui/inputBox';
import { FocusArea } from '@platform-ui/inputTools';
import Scroll, { ScrollBar } from '@platform-ui/scroll';
import { withValueInputState } from '@platform-ui/stateUtils';
import { useMergeRefs } from '@tui-react/core';
import { Counter } from './Counter';
import { TextareaValue } from './TextareaValue/TextareaValue';
import { useDetachedScrollBar } from './useDetachedScrollBar';
import styles from './Textarea.module.css';
/**
 * Высота строки для текстареи
 */
const lineHeights = {
  LINE_HEIGHT_L: 24,
  LINE_HEIGHT_M: 20,
  LINE_HEIGHT_S: 20
};

/**
 * Возвращает высоту строки на основе пропа size, по умолчанию "l", 24px
 */
const getLineHeight = size => {
  switch (size) {
    case 's':
      return lineHeights.LINE_HEIGHT_S;
    case 'm':
      return lineHeights.LINE_HEIGHT_M;
    default:
      return lineHeights.LINE_HEIGHT_L;
  }
};
export const TextareaStateless = /*#__PURE__*/forwardRef((props, forwardedRef) => {
  const {
    dataQaType = 'uikit/textarea',
    dataQaData,
    id,
    name,
    value,
    placeholder,
    disabled,
    readOnly,
    rows,
    minRows = 3,
    maxRows,
    maxLength,
    size = 'l',
    label,
    error,
    valid,
    filled,
    focused,
    cleanable,
    cleanButtonAriaLabel,
    required,
    showAsterisk,
    flatCorners,
    hintBlock,
    leftContent,
    rightContent,
    cobrowsingSecure,
    ariaControls,
    refCallback,
    ariaAutocomplete,
    ariaErrorMessage,
    ariaDescribedBy,
    ariaLabel,
    ariaLabelledby,
    onClean,
    onChange,
    onClick,
    onFocus,
    onBlur,
    onKeyPress,
    onInput,
    onScroll,
    variant,
    highlightExtraSymbols,
    showLengthCounter = maxLength !== undefined,
    UNSAFE_className,
    UNSAFE_style,
    spellCheck,
    transparent
  } = props;
  const labelId = useId(props.labelId);
  const mobile = variant === 'mobile';
  const lineHeight = mobile ? 20 : getLineHeight(size);
  const handleChange = event => {
    onChange(event, {
      value: event.target.value,
      name
    });
  };
  const handleClick = event => {
    onClick == null || onClick(event, {
      name,
      value
    });
  };
  const handleKeyDown = event => {
    const {
      preventReturn,
      onKeyDown
    } = props;
    if (preventReturn && event.keyCode === 13) {
      event.preventDefault();
    }
    onKeyDown == null || onKeyDown(event, {
      name,
      value
    });
  };
  const handleFocus = event => {
    onFocus(event, {
      name,
      value
    });
  };
  const handleBlur = event => {
    onBlur(event, {
      name,
      value
    });
  };
  const handleInput = event => {
    onInput == null || onInput(event, {
      name,
      value
    });
  };
  const handleKeyPress = event => {
    onKeyPress == null || onKeyPress(event, {
      name,
      value
    });
  };
  const [containerProps, scrollProps, scrollBarProps] = useDetachedScrollBar();
  const maxLengthError = value.length > Number(maxLength);
  const textarea = /*#__PURE__*/React.createElement(TextareaValue, {
    focused: focused,
    maxLength: maxLength,
    maxLengthError: maxLengthError,
    highlightExtraSymbols: highlightExtraSymbols,
    "aria-controls": ariaControls,
    "aria-autocomplete": ariaAutocomplete,
    "aria-errormessage": ariaErrorMessage,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": error,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledby || labelId,
    ref: useMergeRefs([forwardedRef, refCallback]),
    className: styles.textarea,
    id: id,
    name: name,
    dataQaType: dataQaType,
    placeholder: placeholder,
    disabled: disabled,
    required: required,
    readOnly: readOnly,
    value: value,
    onChange: handleChange,
    onClick: handleClick,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onInput: handleInput,
    onKeyDown: handleKeyDown,
    onKeyPress: handleKeyPress,
    style: {
      minHeight: Number(rows === undefined ? minRows : rows) * lineHeight
    },
    rows: minRows,
    cobrowsingSecure: cobrowsingSecure,
    whiteSpace: "pre-wrap",
    spellCheck: spellCheck
  });
  const scrollBar = /*#__PURE__*/React.createElement(ScrollBar, scrollBarProps);
  const counter = minRows && minRows > 1 && maxLength && showLengthCounter ? /*#__PURE__*/React.createElement(Counter, {
    length: value.length,
    maxLength: maxLength
  }) : null;
  return /*#__PURE__*/React.createElement(FocusArea, Object.assign({
    className: styles.root
  }, dataQa({
    dataQaType,
    dataQaData
  }), containerProps), /*#__PURE__*/React.createElement(InputBox
  // @TODO исправить в мажорном обновлении. Необходимо убрать проверку dataQaType === 'uikit/textarea'
  // т.к. она была добавлена только для обратной совместимости
  , {
    dataQaType: !dataQaType || dataQaType === 'uikit/textarea' ? undefined : `${dataQaType}.inputBox`,
    rightContent: rightContent,
    leftContent: leftContent,
    flatCorners: flatCorners,
    label: label,
    error: error || maxLengthError,
    expandable: true,
    filled: filled || !!value,
    focused: focused,
    readOnly: readOnly,
    disabled: disabled,
    cleanable: cleanable,
    cleanButtonAriaLabel: cleanButtonAriaLabel,
    required: required,
    showAsterisk: showAsterisk,
    hintBlock: hintBlock,
    labelId: labelId,
    size: size,
    valid: valid && !error && !maxLengthError,
    counter: counter,
    scrollBar: scrollBar,
    variant: variant,
    UNSAFE_className: UNSAFE_className,
    UNSAFE_style: UNSAFE_style,
    onClean: onClean,
    preserveSpaceForMobileCleanButton: mobile,
    transparent: transparent
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.wrapper
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.textareaContainer,
    style: {
      height: rows ? Number(rows) * lineHeight : undefined,
      maxHeight: maxRows ? maxRows * lineHeight : undefined
    }
  }, rows || maxRows ?
  /*#__PURE__*/
  // fallback forces to use scroll wrapper
  React.createElement(Scroll, Object.assign({}, scrollProps, {
    onScroll: onScroll,
    fallback: true
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.scrollContent
  }, textarea)) : textarea))));
});
const Textarea = withValueInputState(TextareaStateless, {
  emptyValue: ''
});
export default Textarea;